import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";

/**
 * Auth callback page — receives `#session_id=...` in URL fragment after the
 * user completes Google OAuth via Emergent Auth. Exchanges the session_id
 * for our JWT via /api/auth/google-session, then redirects to `?next=` or /dashboard.
 *
 * Uses useRef to guard against React StrictMode double-invoke.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { loginWithGoogleSession } = useAuth();
  const processedRef = useRef(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setErr("Missing session id");
      setTimeout(() => nav("/login"), 1500);
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    // Strip the fragment so it isn't reused
    try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* ignore */ }

    const params = new URLSearchParams(loc.search);
    const next = params.get("next") || "/dashboard";

    loginWithGoogleSession(sessionId)
      .then(() => {
        toast.success("Signed in with Google!");
        nav(next, { replace: true });
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Could not sign in with Google";
        setErr(msg);
        toast.error(msg);
        setTimeout(() => nav("/login"), 1800);
      });
  }, [loginWithGoogleSession, nav, loc.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0908] text-[#F4E5BC]" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-10 h-10 border-2 rounded-full animate-spin mx-auto" style={{ borderColor: "#2a2620", borderTopColor: "#D4AF37" }} />
        <div className="mt-4 text-xs uppercase tracking-[0.4em]" style={{ color: "#D4AF37" }}>
          {err ? "Redirecting…" : "Signing you in…"}
        </div>
        {err && <div className="mt-2 text-sm text-rose-400">{err}</div>}
      </div>
    </div>
  );
}
